import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserMd, faGraduationCap, faLanguage, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import './DoctorDetail.css';

const DoctorDetail = () => {
  const { doctorId } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAnimated, setIsAnimated] = useState(false);
  
  // Animation refs
  const profileRef = useRef(null);
  const bioRef = useRef(null);
  const interestsRef = useRef(null);
  const certificationsRef = useRef(null);
  const ctaRef = useRef(null);

  // Mock doctors data (same as in Doctors.js)
  const mockDoctors = [
    {
      id: 'dr-ahmed-shetia',
      name: 'Dr. Ahmed Shetia',
      specialty: 'General Practitioner',
      qualifications: 'MBBS, FRACGP, Dip of Skin Cancer Medicine and Surgery. ED diploma ACEM, Msc Paediatric. Authorised prescriber for medicinal cannabis',
      image: '/images/DR-AHMED-SHETIA_flat-1.jpg',
      shortBio: 'With over 15 years of diverse medical experience across Egypt, Oman, and Australia, Dr. Shetia brings expertise in general practice, paediatrics, emergency medicine, and critical care.',
      fullBio: 'With over 15 years of diverse medical experience across Egypt, Oman, and Australia, I bring a broad clinical background in general practice, paediatrics, emergency medicine, and critical care. I completed Paediatric ICU training in Egypt and worked as a paediatrician before moving to Oman, where I served as a GP in one of the country\'s largest rural hospitals for three years. Since relocating to Australia in 2017, I have worked as an ICU registrar at Blacktown Hospital, followed by over three years as a medical and emergency registrar at Maitland and John Hunter Hospitals. I later held a senior emergency medicine role at Belmont Hospital for two years. Most recently, I completed my GP training over the past three years at a busy general practice clinic in Cessnock. I am fluent in both English and Arabic and am known for being compassionate, knowledgeable, and committed to long-term patient care. Outside of work, I enjoy reading, movies, bushwalking, and travelling with my family.',
      languages: ['English', 'Arabic'],
      specialInterests: ['Chronic Disease Management', 'Medicinal Cannabis', 'Mental Health', 'Anxiety and PTSD', 'Chronic Pain', 'Sleep Disorders'],
      availability: 'Mondays and Wednesdays',
      certifications: [
        'AMC Certificate',
        'Diploma in Advancing Emergency Medicine – ACEM',
        'TGA Authorised Prescriber Scheme (APS) – Medicinal Cannabis',
        'Professional Certificate in Skin Cancer Medicine',
        'Skin Cancer Cert, Dip of Skin Cancer Medicine and Surgery',
        'Dermoscopy Cert',
        'MSc Paediatric, Ainshams Uni, Egypt',
        'ALS1 & ALS2 (Advanced Life Support)',
        'Advanced Airway Management',
        'Advanced Injectable Iron and Vitamin Therapies'
      ]
    },
    {
      id: 'dr-mohammed',
      name: 'Dr. Mohammed Mohammed',
      specialty: 'General Practitioner',
      qualifications: 'MBBS Grad Cert. Clin. Ultrasound (Melb) FACRRM EM AST',
      image: '/images/Mohammed profile.jpg',
      shortBio: 'Dr Mohammed Mohammed holds Fellowship with ACRRM and has 10 years of experience as a doctor. His special interests include skin cancer surgery, point-of-care ultrasonography and pain management.',
      fullBio: 'Dr Mohammed Mohammed holds Fellowship with ACRRM and has 10 years of experience as a doctor. His other roles revolve around being a GP Supervisor and a Medical Educator whilst his special interests include skin cancer surgery, point-of-care ultrasonography and pain management. He is highly regarded amongst his peers for his professional approach and equally so amongst his patients as an attentive practitioner.',
      languages: ['English', 'Arabic', 'Kurdish'],
      specialInterests: ['Skin Cancer Surgery', 'Point-of-Care Ultrasonography', 'Pain Management', 'Medical Education'],
      availability: 'Every other Tuesday and Fridays'
    },
    {
      id: 'dr-karan-sian',
      name: 'Dr. Karan Sian',
      specialty: 'GP Registrar',
      qualifications: 'MBBS BSc Hons',
      image: '/images/Dr Karan Sian.jpg',
      shortBio: 'Dr Karan Sian is a GP Registrar working toward Fellowship with the Australian College of Rural and Remote Medicine. He has over 15 years\' experience as a medical professional.',
      fullBio: 'Dr Karan Sian is a GP Registrar working toward Fellowship with the Australian College of Rural and Remote Medicine. Dr Karan has over 15 years\' experience as a medical professional. He has extensive experience in the fields of intensive care, emergency and surgery. His well-honed surgical skills coupled with a keen interest in skin cancer surgery provide all-round surgical care at the practice.',
      languages: ['English'],
      specialInterests: ['Intensive Care', 'Emergency Medicine', 'Surgery', 'Skin Cancer Surgery'],
      availability: 'Monday to Friday'
    },
    {
      id: 'dr-shaimaa-elgayar',
      name: 'Dr. Shaimaa Elgayar',
      specialty: 'General Practitioner',
      qualifications: 'MBBS, FRACGP, RACGP Dermatology Cert, Dip of Skin Cancer Medicine and Surgery',
      image: '/images/shaimaa.jpg',
      shortBio: 'Dr. Shaimaa Elgayar is a GP specialist family physician and fellow of the RACGP with nearly two decades of international medical experience.',
      fullBio: 'Dr. Shaimaa Elgayar is a GP specialist family physician and fellow of the RACGP who is highly experienced medical professional with a diverse and international career spanning nearly two decades. Since earning her medical degree in 2004, she has practiced medicine across multiple countries, including Australia, where she has become a trusted and respected figure in general practitioners. Dr. Shaimaa has worked in various capacities as a Career Medical Officer (CMO) across different departments at local hospitals in Newcastle, Australia, including General Medicine and Medical Oncology at Calvary Mater Hospital, and Emergency Medicine at John Hunter Hospital and Tomaree Hospital. Her dermatology expertise, particularly in skin cancer, is complemented by an RACGP Certificate in Dermatology. Dr. Shaimaa has undergone advanced training in skin cancer surgeries, including techniques such as flaps and grafts, and is currently working toward full accreditation with the Skin Cancer College of Australia.',
      languages: ['English', 'Arabic', 'French'],
      specialInterests: ['Skin Cancer Medicine and Surgery', 'Aesthetic Medicine', 'Chronic Diseases', 'Mental Health', 'Women\'s Health', 'Medicinal Cannabis'],
      availability: 'Every other Teusdays and Thursdays',
      quote: 'True healing begins when we listen, understand, and treat each patient as a whole person—not just a condition.'
    },
    {
      id: 'dr-tasnuba-mir',
      name: 'Dr. Tasnuba Mir',
      specialty: 'Gp Registrar',
      qualifications: 'MBBS, AMC',
      image: '/images/Tasnuba.jpg',
      shortBio: 'Dr. Tasnuba Mir is a dedicated General Practitioner with 7 years of experience in providing comprehensive general practitioners services.',
      fullBio: 'Dr. Tasnuba Mir is a dedicated General Practitioner with 7 years of experience in providing comprehensive general practitioners services. She completed her medical degree and has been committed to delivering patient-centered care throughout her career. Dr. Mir has a particular interest in women\'s health, preventive medicine, and chronic disease management. She is known for her compassionate approach and takes time to listen to her patients\' concerns, ensuring they receive personalized care tailored to their individual needs.',
      languages: ['English', 'Bengali'],
      specialInterests: ['Women\'s Health', 'Preventive Medicine', 'Chronic Disease Management', 'Family Medicine'],
      availability: 'Maternity leave'
    },
    {
      id: 'dr-ahmed-elakabawey',
      name: 'Dr. Ahmed Elakabawey',
      specialty: 'General Practitioner',
      qualifications: 'GP, FRACGP',
      image: '/images/dr-ahmed-al-akabawy .jpg',
      shortBio: 'Dr Ahmed Elakabawey is a fully qualified and experienced General Practitioner holding Fellowship with the Royal Australian College of General Practitioners (FRACGP).',
      fullBio: 'Dr Ahmed Elakabawey is a fully qualified and experienced General Practitioner holding Fellowship with the Royal Australian College of General Practitioners (FRACGP). He provides comprehensive care for patients of all ages, with expertise in preventive health, chronic disease management, and acute care. Known for his approachable manner and patient-centred approach, Dr Elakabawey is committed to delivering evidence-based, compassionate General Practitioner care.',
      languages: ['English', 'Arabic'],
      specialInterests: ['Preventive Health', 'Chronic Disease Management', 'Acute Care', 'Patient-Centred Care'],
      availability: 'Telehealth only'
    },
    {
      id: 'dr-patrick-corcoran',
      name: 'Dr. Patrick Corcoran',
      specialty: 'GP Registrar',
      qualifications: 'ACRRM Registrar (Australian College of Rural and Remote Medicine)',
      image: '/images/dr-patrick-corcoran.jpeg',
      shortBio: 'Dr Patrick is a Registrar with the Australian College of Rural and Remote Medicine (ACRRM). He has a passion for Rural Health and Community focus. Currently accepting new patients.',
      fullBio: 'Dr Patrick is a Registrar with the Australian College of Rural and Remote Medicine (ACRRM). He has a passion for Rural Health and Community focus. Having completed Internship and Residency in the Hunter New England Region, he has returned to continue and expand his training as a Rural Generalist.',
      languages: ['English'],
      specialInterests: ['Sports Medicine and Concussion Management', 'Indigenous Health', 'Emergency Medicine', 'Rural and Regional Retrieval Medicine'],
      availability: 'Accepting new patients'
    }
  ];

  useEffect(() => {
    // Find doctor by ID
    const foundDoctor = mockDoctors.find(doc => doc.id === doctorId);
    if (foundDoctor) {
      setDoctor(foundDoctor);
    }
    setLoading(false);
  }, [doctorId]);

  // Animation effect
  useEffect(() => {
    if (!doctor || loading) return;

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const element = entry.target;
          
          if (element === profileRef.current) {
            element.classList.add('animate-profile');
          } else if (element === bioRef.current) {
            element.classList.add('animate-bio');
          } else if (element === interestsRef.current) {
            element.classList.add('animate-interests');
          } else if (element === certificationsRef.current) {
            element.classList.add('animate-certifications');
          } else if (element === ctaRef.current) {
            element.classList.add('animate-cta');
          }
          
          observer.unobserve(element);
        }
      });
    }, observerOptions);

    // Observe elements
    const elements = [profileRef, bioRef, interestsRef, certificationsRef, ctaRef];
    elements.forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    // Trigger initial animation
    setTimeout(() => setIsAnimated(true), 100);

    return () => {
      elements.forEach(ref => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
  }, [doctor, loading]);

  if (loading) {
    return (
      <div className="doctor-detail-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading doctor information...</p>
        </div>
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="doctor-detail-page">
        <div className="container">
          <div className="not-found">
            <h2>Doctor Not Found</h2>
            <p>The doctor you are looking for does not exist.</p>
            <Link to="/doctors" className="btn-primary">
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Doctors
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="doctor-detail-page">
      <Helmet>
        <title>{doctor ? `${doctor.name} | Tea Gardens GP` : 'Doctor Profile | Tea Gardens GP'}</title>
        <meta name="description" content={doctor ? `Learn about ${doctor.name}, ${doctor.specialty} at Tea Gardens GP. ${doctor.shortBio}` : 'Doctor profile at Tea Gardens GP - Professional medical care in NSW'} />
        <link rel="canonical" href={`https://teagardensgp.com.au/doctors/${doctorId}`} />
      </Helmet>
      {/* Back Navigation */}
      <div className="back-nav">
        <div className="container">
          <Link to="/doctors" className="back-link">
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Doctors
          </Link>
        </div>
      </div>

      {/* Doctor Profile & Biography Combined */}
      <section className="section doctor-profile-combined">
        <div className="container-fluid">
          <div className="doctor-main-content" ref={profileRef} id="doctor-profile-section">
            {/* Left Side - Doctor Profile */}
            <div className="doctor-profile-section">
              <div className="doctor-image-section">
                <img src={doctor.image} alt={doctor.name} className="doctor-image" />
              </div>
              
              <div className="doctor-info-section">
                <h1 className="doctor-name">{doctor.name}</h1>
                <p className="doctor-specialty">
                  <FontAwesomeIcon icon={faUserMd} /> {doctor.specialty}
                </p>
                <p className="doctor-qualifications">
                  <FontAwesomeIcon icon={faGraduationCap} /> {doctor.qualifications}
                </p>

                <div className="doctor-details">
                  <div className="detail-row">
                    <FontAwesomeIcon icon={faLanguage} />
                    <span><strong>Languages:</strong> {doctor.languages.join(', ')}</span>
                  </div>

                </div>

                <div className="action-buttons">
                  <Link to="https://www.hotdoc.com.au/medical-centres/tea-gardens-NSW-2324/tea-gardens-gp/doctors" className="btn-primary">Book Appointment</Link>
                  <Link to="/contact" className="btn-secondary">Contact Doctor</Link>
                </div>
              </div>
            </div>

            {/* Right Side - Doctor Biography */}
            <div className="doctor-bio-section" ref={bioRef} id="doctor-bio-section">
              <h2>About {doctor.name}</h2>
              <p className="bio-text">{doctor.fullBio}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Special Interests */}
      <section className="section special-interests" ref={interestsRef} id="special-interests-section">
        <div className="container-fluid">
          <h2>Areas of Special Interest</h2>
          <div className="interests-grid">
            {doctor.specialInterests.map((interest, index) => (
              <div key={index} className="interest-card">
                <h3>{interest}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education & Certifications */}
      {doctor.certifications && (
        <section className="section certifications" ref={certificationsRef} id="certifications-section">
          <div className="container-fluid">
            <h2>Education & Certifications</h2>
            <div className="certifications-grid">
              {doctor.certifications.map((cert, index) => (
                <div key={index} className="certification-card">
                  <FontAwesomeIcon icon={faGraduationCap} />
                  <span>{cert}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Call to Action */}
      <section className="section cta-section" ref={ctaRef} id="cta-section">
        <div className="container-fluid">
          <div className="cta-content">
            <h2>Ready to Book with {doctor.name}?</h2>
            <p>Schedule your appointment today for personalized general practice care.</p>
            <Link to="https://www.hotdoc.com.au/medical-centres/tea-gardens-NSW-2324/tea-gardens-gp/doctors" className="btn-primary">Book Appointment</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DoctorDetail;
